import { isEmpty } from 'lodash'
import { questionType } from '@edulastic/constants'
import { getCards } from '../PickUpQuestionType/components/QuestionType/constants'
import { getLanguageKeys } from '../../common/utils/helpers'
import { convertClozeDragDropToClassification } from './questionTypeConverters/clozeDragDropToClassification'

const {
  MULTIPART,
  CLOZE_DRAG_DROP,
  CLASSIFICATION,
  useLanguageFeatureQn,
} = questionType

const removeResponseBoxesFromStimulus = (stimulus) => {
  if (!window.$ || !stimulus || !stimulus.trim().length) return stimulus
  const jqueryEl = $('<p>').append(stimulus)
  const tagsToRemove = [
    'mathinput',
    'mathunit',
    'textinput',
    'textdropdown',
    'response',
  ]
  tagsToRemove.forEach((tagToRemove) => {
    // eslint-disable-next-line func-names
    jqueryEl.find(tagToRemove).each(function () {
      $(this).replaceWith(' ')
    })
  })
  return jqueryEl.html()
}

export const getNewQuestionTypeData = ({
  currentQuestionData,
  newQuestionType,
  isPremiumUser,
}) => {
  const allQuestionData = getCards()
  let { data: newQuestionData = {} } =
    allQuestionData.find(
      (question) => question?.data?.type === newQuestionType
    ) || {}

  const {
    id,
    stimulus = '',
    instructorStimulus,
    sampleAnswer,
    hints = [],
    metadata: { distractor_rationale_response_level: distractors = [] } = {},
    alignment,
    grades,
    subjects,
    depthOfKnowledge,
    authorDifficulty,
    bloomsTaxonomy,
    tags,
    testletQuestionId,
    testletResponseIds,
    testletAdditionalMetadata,
    rubrics,
    languageFeatures = {},
    validation: {
      validResponse: { score: currentQuestionScore = 1 } = {},
    } = {},
  } = currentQuestionData || {}

  const languageCodes = getLanguageKeys({ languageFeatures })

  let hasLanguageContent = false
  const languageFeaturesWithData = {}
  for (const languageCode of languageCodes) {
    const _stimulus = languageFeatures?.[languageCode]?.stimulus || ''
    const _hints = languageFeatures?.[languageCode]?.hints || []
    const _instructorStimulus =
      languageFeatures?.[languageCode]?.instructorStimulus || ''
    const _sampleAnswer = languageFeatures?.[languageCode]?.sampleAnswer || ''

    hasLanguageContent =
      _stimulus || _hints.length || _instructorStimulus || _sampleAnswer

    if (hasLanguageContent) {
      languageFeaturesWithData[languageCode] = {
        stimulus: _stimulus,
        hints: _hints,
        instructorStimulus: _instructorStimulus,
        sampleAnswer: _sampleAnswer,
      }
    }
  }

  const languageFeaturesData = {
    languageFeatures: languageFeaturesWithData,
  }

  const metadata = { distractor_rationale_response_level: distractors }

  const hasLanguageFeatures =
    isPremiumUser &&
    useLanguageFeatureQn.includes(newQuestionType) &&
    hasLanguageContent

  if (!isEmpty(newQuestionData)) {
    newQuestionData = {
      id,
      ...newQuestionData,
      stimulus: removeResponseBoxesFromStimulus(stimulus),
      ...(instructorStimulus ? { instructorStimulus } : {}),
      ...(sampleAnswer ? { sampleAnswer } : {}),
      ...(hints?.length ? { hints } : {}),
      ...(distractors?.length ? { metadata } : {}),
      ...(alignment ? { alignment } : {}),
      ...(grades ? { grades } : {}),
      ...(subjects ? { subjects } : {}),
      ...(depthOfKnowledge ? { depthOfKnowledge } : {}),
      ...(authorDifficulty ? { authorDifficulty } : {}),
      ...(bloomsTaxonomy ? { bloomsTaxonomy } : {}),
      ...(tags ? { tags } : {}),
      ...(testletQuestionId ? { testletQuestionId } : {}),
      ...(testletResponseIds ? { testletResponseIds } : {}),
      ...(testletAdditionalMetadata ? { testletAdditionalMetadata } : {}),
      /** On switching question type copy languageFeaturesData to new question type */
      ...(hasLanguageFeatures ? languageFeaturesData : {}),
    }

    if (
      currentQuestionData?.type === CLOZE_DRAG_DROP &&
      newQuestionType === CLASSIFICATION
    ) {
      newQuestionData = {
        ...newQuestionData,
        ...(convertClozeDragDropToClassification(currentQuestionData) || {}),
      }
    }

    if (!isEmpty(rubrics)) {
      newQuestionData.rubrics = rubrics
      newQuestionData.validation.validResponse.score = currentQuestionScore
    }
  }

  return newQuestionData
}

export const getUpdatedItemData = ({
  currentItemData,
  newQuestionData,
  newQuestionType,
}) => {
  const updatedItemData = {
    ...currentItemData,
    rows: [
      {
        tabs: [],
        dimension: '100%',
        widgets: [
          {
            widgetType: 'question',
            type: newQuestionData.type,
            title: newQuestionData.title,
            reference: newQuestionData.id,
            tabIndex: 0,
          },
        ],
        flowLayout: false,
        content: '',
      },
    ],
    data: {
      ...newQuestionData.data,
      questions: [{ ...newQuestionData }],
    },
    multipartItem: newQuestionType === MULTIPART,
  }

  return updatedItemData
}

export const getPathAndRouterState = ({
  newQuestionType,
  testItemId,
  testId,
  isRegradeFlow,
  isTestFlow,
  history,
  match,
  i18Translate,
}) => {
  let routerState = history?.location?.state || {}
  let backButtonPath = ''
  let currentPath = ''
  const isNewTypeMultipart = newQuestionType === MULTIPART

  if (testItemId === 'new') {
    if (isTestFlow || isRegradeFlow) {
      backButtonPath = `/author/tests/${testId}/createItem/new/pickup-questiontype`
      currentPath = isNewTypeMultipart
        ? `/author/tests/${testId}/createItem/new`
        : `/author/tests/${testId}/createItem/new/questions/create/${newQuestionType}`
    } else {
      backButtonPath = `/author/items/new/pickup-questiontype`
      currentPath = isNewTypeMultipart
        ? '/author/items/new/item-detail'
        : `/author/questions/create/${newQuestionType}`
      routerState = {
        ...routerState,
        backUrl: match.url,
        backText: i18Translate('component.pickupcomponent.headertitle'),
      }
    }
  }

  if (testItemId !== 'new' && !isRegradeFlow) {
    if (isTestFlow) {
      backButtonPath = `/author/tests/${testId}/createItem/new/pickup-questiontype`
    } else {
      backButtonPath = `/author/items/new/pickup-questiontype`
    }
    currentPath = match.url
  }

  if (testItemId !== 'new' && isRegradeFlow) {
    backButtonPath = `/author/tests/${testId}/createItem/new/pickup-questiontype`
    currentPath = isNewTypeMultipart
      ? `/author/tests/${testId}/createItem/new`
      : `/author/tests/${testId}/createItem/new/questions/create/${newQuestionType}`
    routerState = {
      ...routerState,
      rowIndex: 0,
      tabIndex: 0,
    }
  }

  if (routerState.quickLinkQuestionType) {
    backButtonPath = isTestFlow
      ? `/author/tests/tab/review/id/${testId}`
      : '/author/items'
  }
  return { backButtonPath, currentPath, routerState }
}
